<template>
  <div>
    <div v-if="isArchive && loadError" class="flex flex-col items-center gap-4 p-8">
      <p class="text-lg text-gray-300">No puzzle available for this day.</p>
      <button @click="exitArchive"
              class="bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 text-lg text-gray-900 px-6 py-2 rounded">
        Back to today
      </button>
    </div>
    <template v-else>
      <!-- Whole-screen ambiance that intensifies with the momentum multiplier (live game only). -->
      <MomentumAura v-if="!isArchive" :multiplier="momentumMult"/>
      <!-- Red heartbeat vignette when the pool is down to its last heart: makes the core stake felt. -->
      <DangerAura :active="lowHearts"/>
      <!-- Archive practice banner: makes clear this is a past day and nothing is at stake. -->
      <div v-if="isArchive" class="flex flex-row items-center justify-between gap-2 w-full max-w-sm mx-auto mt-3 mb-2 px-3 py-1.5 rounded bg-gray-800 border border-gray-700 text-sm">
        <span class="min-w-0 truncate">
          <span class="font-semibold text-gray-100">{{ archiveDateLabel }}</span>
          <span class="text-gray-500"> · no pressure 😌</span>
        </span>
        <button @click="exitArchive" class="shrink-0 text-teal-400 hover:text-teal-300 whitespace-nowrap">
          Back to today
        </button>
      </div>
    <div id="game" class="game flex flex-col items-center justify-center">
      <div class="flex flex-row mt-2-on-short-screen mb-2 sm:mb-6 text-2xl text-green-400">
        <Trophy :is-score-popping="isScorePopping" :score="score"/>
      </div>


      <div class="board" :class="{ 'board-damage': isShaking }">
        <div v-for="row in grid" class="row flex flex-row items-center">
          <div v-for="tile in row"
               class="tile w-9 h-9 w-8-on-short-screen h-8-on-short-screen border-2 rounded m-1 text-xl font-bold flex items-center justify-center"
               :class="{
                 invisible: !tile.letter,
                 'bg-gray-800 border-gray-600': tile.letter && !tile.found,
                 'bg-gradient-to-br from-teal-400 to-green-500 border-transparent text-gray-900': tile.found,
                 'flip-animation': isTilePopping(tile)
               }">
            {{ tile.found ? tile.letter : '' }}
          </div>
        </div>
      </div>

      <div class="flex flex-row mb-2 sm:mb-6 mt-2 hearts-row-on-short-screen">
        <TransitionGroup name="hearts">
          <div v-for="normal in heartCount" :key="normal" class="text-2xl p-0.5 text-red-600"
               :class="{ 'last-heart': lowHearts }">
            <Heart/>
          </div>
          <!-- Ghost slot: whispers that a word commit can bank a heart right now, no copy on the board. -->
          <div v-if="showGhostHeart" key="ghost" class="ghost-heart text-2xl p-0.5">
            <Heart/>
          </div>
        </TransitionGroup>
      </div>
      <MomentumMeter v-if="!isArchive && !puzzleFinished" :momentum="momentum" :multiplier="momentumMult"/>
      <div class="flex row" v-if="puzzleFinished">
        <div v-if="!failedGame">
          <div class="p-2 pb-6">Nice job! 🥳 Get the next one!</div>
          <button class="bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 text-xl text-gray-900 w-60 py-2"
                  :disabled="loading" @click="nextGame()">Next Game
          </button>
        </div>
        <!-- Live runs show the daily leaderboard; archive runs are leaderboard-free, so
             show a lightweight practice result instead. -->
        <GameOver v-if="!isArchive && failedGame && resultsReady" :score="score" :report="runReport"></GameOver>
        <div v-if="isArchive && failedGame && resultsReady" class="flex flex-col items-center gap-3">
          <div class="text-xl p-2">{{ score === seriesLength ? 'Perfect run! 🏆' : 'Run over!' }}</div>
          <div class="text-gray-300">You solved {{ score }} this run.</div>
          <button class="bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 text-lg text-gray-900 w-60 py-2"
                  @click="replayArchive">Play again
          </button>
          <button class="text-teal-400 hover:text-teal-300 py-1" @click="exitArchive">Back to today</button>
        </div>
      </div>
      <LoadingSpinner v-if="loading"></LoadingSpinner>

      <!-- Word-guess bar. Out of word mode it's just an entry button (with the heart-stakes
           badge); in word mode the on-screen keyboard spells into the tray, so the native
           keyboard never opens and the grid stays in view. -->
      <div v-if="!puzzleFinished" class="flex flex-row items-center justify-center mt-1 mb-3 gap-2 guess-row-on-short-screen">
        <template v-if="!wordMode">
          <!-- Spend a heart to reveal a hidden letter. Hidden until it's actually usable
               (mid-puzzle, >1 heart, a letter left to reveal) so it never dangles disabled. -->
          <div v-if="hintAvailable" class="relative inline-flex">
            <button
                @click="useHint"
                class="guess-control-on-short-screen h-11 px-5 bg-gray-700 text-teal-300 text-lg font-semibold rounded-lg border border-teal-400/60 shadow-md cursor-pointer transition active:scale-95 hover:brightness-110">
              Hint 💡
            </button>
            <span class="absolute -top-2 -right-2 flex items-center gap-0.5 text-[10px] leading-none font-bold bg-gray-900 ring-1 ring-teal-400 rounded-full px-1.5 py-0.5 pointer-events-none">
              <span class="text-red-400">-1</span>
              <span class="text-[8px] -ml-px"><Heart/></span>
            </span>
          </div>
          <div class="relative inline-flex">
            <button
                @click="enterWordMode"
                class="guess-control-on-short-screen h-11 px-6 bg-gradient-to-t from-teal-400 to-green-400 text-gray-900 text-lg font-semibold rounded-lg border-0 shadow-md cursor-pointer transition active:scale-95 hover:brightness-110">
              Guess a word
            </button>
            <!-- Stakes badge, mirroring the 5x XP badge on the login button: a correct word
                 banks a heart, a wrong one costs two. -->
            <span class="absolute -top-2 -right-2 flex items-center gap-0.5 text-[10px] leading-none font-bold bg-gray-900 ring-1 ring-green-400 rounded-full px-1.5 py-0.5 pointer-events-none">
              <span class="text-green-400">+1</span>
              <span class="text-gray-500">/</span>
              <span class="text-red-400">-2</span>
              <span class="text-[8px] -ml-px"><Heart/></span>
            </span>
          </div>
        </template>
        <template v-else>
          <button
              @click="exitWordMode"
              title="Cancel"
              class="guess-control-on-short-screen h-11 w-11 flex items-center justify-center bg-gray-700 text-gray-300 text-xl rounded-lg border-0 shadow-md cursor-pointer transition active:scale-95 hover:brightness-110">
            &times;
          </button>
          <div class="guess-control-on-short-screen h-11 w-36 px-3 flex items-center justify-center bg-gray-700 rounded-lg border-2 border-teal-400 shadow-md uppercase tracking-widest text-white text-lg">
            <span v-if="wordGuess">{{ wordGuess }}</span>
            <span v-else class="text-gray-400 normal-case tracking-normal text-base">Spell a word</span>
          </div>
          <button
              @click="backspace"
              :disabled="!wordGuess"
              title="Delete"
              class="guess-control-on-short-screen h-11 w-11 flex items-center justify-center bg-gray-700 text-gray-300 text-xl rounded-lg border-0 shadow-md cursor-pointer transition active:scale-95 hover:brightness-110 disabled:opacity-40 disabled:cursor-default">
            &#9003;
          </button>
          <button
              @click="submitWord"
              :disabled="!wordGuess.trim()"
              class="guess-control-on-short-screen h-11 px-5 bg-gradient-to-t from-teal-400 to-green-400 text-gray-900 text-lg font-semibold rounded-lg border-0 shadow-md cursor-pointer transition active:scale-95 hover:brightness-110 disabled:opacity-40 disabled:cursor-default">
            Guess
          </button>
        </template>
      </div>

      <div v-if="!puzzleFinished" v-for="alphabet in alphabetRows" class="row flex flex-row">
        <button v-for="letter in alphabet"
                class="tile kb-key-on-short-screen w-8 h-11 p-0 bg-gray-600 mx-0.5 my-1 text-xl font-semibold rounded cursor-pointer select-none text-center border-0 shadow-md transition active:scale-95 hover:brightness-110"
                :class="{ 'bg-gradient-to-t from-pink-600 to-red-600' : isLetterAWrongGuess(letter), 'bg-gradient-to-t from-teal-400 to-green-400 text-gray-900': isLetterACorrectGuess(letter)}"
                @click="onKey(letter)">
          {{ letter }}
        </button>
      </div>
    </div>
    <XpBar v-if="puzzleFinished && !isArchive" :xp="xp" :isPopping="isScorePopping"></XpBar>
    </template>
  </div>
</template>
<script setup>
import {onMounted, onUnmounted, ref, watch, computed} from 'vue'
import {useGameEngine} from "../composables/useGameEngine.js";
import {useArchive, createArchiveAdapter} from "../composables/useArchive.js";
import LoadingSpinner from "./LoadingSpinner.vue";
import GameOver from "./GameOver.vue";
import Heart from "./icons/Heart.vue";
import Trophy from "./icons/Trophy.vue";
import XpBar from "./XpBar.vue";
import MomentumMeter from "./MomentumMeter.vue";
import MomentumAura from "./MomentumAura.vue";
import DangerAura from "./DangerAura.vue";

const props = defineProps({
  msg: String,
  // When set (a `yyyy-M-d` date), this is an archive practice run for that past day.
  archiveDate: {type: String, default: null},
})

const isArchive = computed(() => !!props.archiveDate);
const {exit: exitArchive, replay: replayArchive} = useArchive();

// Archive runs use a local, leaderboard-free adapter; the live game uses the default.
const archiveAdapter = props.archiveDate ? createArchiveAdapter(props.archiveDate) : null;
const loadError = archiveAdapter ? archiveAdapter.loadError : ref(false);

const archiveDateLabel = computed(() => {
  if (!props.archiveDate) return '';
  const [y, m, d] = props.archiveDate.split('-').map(Number);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d)}/${pad(m)}/${y}`;
});

const {
  hearts,
  momentum,
  momentumMult,
  maxHearts,
  seriesLength,
  useHint,
  hintAvailable,
  commitWord,
  eligibleWords,
  puzzleFinished,
  failedGame,
  nextGame,
  isLetterAWrongGuess,
  isLetterACorrectGuess,
  letterClicked,
  grid,
  score,
  correctGuesses,
  loading,
  initialiseGame,
    xp,
  resultsReady,
  wrongGuessNonce,
  runReport
} = useGameEngine(archiveAdapter ?? undefined);

let isScorePopping = ref(false);
let isLetterPopping = ref(false);
// Brief board shake + red glow whenever a guess actually costs hearts, so the mistake lands.
const isShaking = ref(false);
watch(wrongGuessNonce, () => {
  isShaking.value = false;
  // Re-arm on the next frame so consecutive wrong guesses restart the animation.
  requestAnimationFrame(() => {
    isShaking.value = true;
    setTimeout(() => isShaking.value = false, 500);
  });
});

// the tile is part of the most-recently-guessed letter that's currently revealing
function isTilePopping(tile) {
  return isLetterPopping.value
      && tile.found
      && correctGuesses.value[correctGuesses.value.length - 1] === tile.letter;
}

let firstAlphabetRow = ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"];
let secondAlphabetRow = ["A", "S", "D", "F", "G", "H", "J", "K", "L"];
let thirdAlphabetRow = ["Z", "X", "C", "V", "B", "N", "M"];
let alphabetRows = [firstAlphabetRow, secondAlphabetRow, thirdAlphabetRow];
onMounted(() => initialiseGame());

watch(score, (newValue, oldValue) => {
  isScorePopping.value = true;
  setTimeout(() => isScorePopping.value = false, 500);
});

const heartCount = computed(() => Math.max(0, hearts.value));

// Down to the last life mid-puzzle: drives the red heartbeat vignette and the pulse on the
// final heart. Gated on an unfinished puzzle so the tension drops the moment the run resolves.
const lowHearts = computed(() => heartCount.value === 1 && !puzzleFinished.value);

// Show a faint outline heart as the next slot when a word commit could bank one right
// now (some un-found word has 2+ hidden tiles) and the pool isn't already maxed out.
const showGhostHeart = computed(() =>
    eligibleWords.value.length > 0 && heartCount.value < maxHearts
);

const wordGuess = ref('');
const wordMode = ref(false);

function enterWordMode() {
  wordMode.value = true;
}

function exitWordMode() {
  wordMode.value = false;
  wordGuess.value = '';
}

// In word mode the on-screen keyboard spells into the tray (repeats and already-guessed
// letters allowed); otherwise a tap guesses that single letter across the puzzle as before.
function onKey(letter) {
  if (wordMode.value) {
    if (wordGuess.value.length < 7) {
      wordGuess.value += letter;
    }
  } else {
    letterClicked(letter);
  }
}

function backspace() {
  wordGuess.value = wordGuess.value.slice(0, -1);
}

async function submitWord() {
  const value = wordGuess.value;
  exitWordMode();
  await commitWord(value);
}

// Physical-keyboard support while spelling (desktop), so dropping the text input doesn't
// cost typed entry. Only active in word mode; normal letter-guessing stays tap-only.
function handleKeydown(e) {
  if (!wordMode.value) return;
  if (e.key === 'Enter') {
    submitWord();
  } else if (e.key === 'Backspace') {
    e.preventDefault();
    backspace();
  } else if (e.key === 'Escape') {
    exitWordMode();
  } else if (/^[a-zA-Z]$/.test(e.key) && wordGuess.value.length < 7) {
    wordGuess.value += e.key.toUpperCase();
  }
}

onMounted(() => window.addEventListener('keydown', handleKeydown));
onUnmounted(() => window.removeEventListener('keydown', handleKeydown));


watch(correctGuesses, (newValue, oldValue) => {
  if (newValue.length > 1 && oldValue.length === 0) {
    return; // edge case when you refresh screen
  }
  if (newValue.length > oldValue.length) {
    isLetterPopping.value = true;
    setTimeout(() => isLetterPopping.value = false, 500);
  }
});

</script>


<style scoped>
.hearts-move,
.hearts-enter-active {
  transition: all 0.5s ease;
}

.hearts-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

/* A lost heart doesn't just slide away: it flares red and shatters out, so losing a life
   reads as a real cost rather than a quiet disappearance. */
.hearts-leave-active {
  position: absolute;
  animation: heart-break 0.55s ease forwards;
}

@keyframes heart-break {
  0%   { transform: scale(1) rotate(0); opacity: 1; filter: brightness(1); }
  25%  { transform: scale(1.45) rotate(-10deg); filter: brightness(1.9) saturate(1.6); }
  100% { transform: scale(0.15) rotate(14deg) translateY(14px); opacity: 0; }
}

/* Wrong guess: shake the board and pulse a red glow around the tiles. */
.board-damage {
  animation: board-shake 0.5s ease, board-flash 0.5s ease;
}

@keyframes board-shake {
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-8px); }
  30% { transform: translateX(7px); }
  45% { transform: translateX(-6px); }
  60% { transform: translateX(5px); }
  75% { transform: translateX(-3px); }
}

@keyframes board-flash {
  0%   { filter: drop-shadow(0 0 0 rgba(239, 68, 68, 0)); }
  20%  { filter: drop-shadow(0 0 12px rgba(239, 68, 68, 0.9)); }
  100% { filter: drop-shadow(0 0 0 rgba(239, 68, 68, 0)); }
}

/* Last life: the lone remaining heart thumps like a heartbeat, in concert with the DangerAura. */
.last-heart {
  animation: last-heart-beat 1.1s ease-in-out infinite;
}

@keyframes last-heart-beat {
  0%, 100% { transform: scale(1); }
  18% { transform: scale(1.28); }
  34% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

@media (prefers-reduced-motion: reduce) {
  .last-heart {
    animation: none;
  }
}

/* Ghost slot: same heart icon as a filled life, but hollowed out to a faint outline so it
   reads as "a heart you could still earn" rather than one you hold. */
.ghost-heart :deep(.heart-container svg *) {
  fill: transparent !important;
  stroke: rgba(220, 38, 38, 0.55);
  stroke-width: 34;
}


/* Compact the board when vertical space is tight (short windows, landscape, and small
   phones like the iPhone SE at 667px tall). Taller phones keep the full-size layout. */
@media screen and (max-height: 700px) {
  .h-8-on-short-screen {
    height: 2rem;
  }

  .w-8-on-short-screen {
    width: 2rem;
  }

  .mt-2-on-short-screen {
    margin-top: 0rem;
  }

  /* The word-guess row is new vertical content; on short screens shrink it and the
     keyboard so the board still fits without everything bunching up. */
  .hearts-row-on-short-screen {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
  }

  .kb-key-on-short-screen {
    height: 2.25rem;
  }

  .guess-row-on-short-screen {
    margin-top: 0.5rem;
  }

  .guess-control-on-short-screen {
    height: 2.25rem;
  }
}
</style>
